import {
    useCallback,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import type { Ref } from "react";
import classNames from "classnames";
import { FiltersTypes } from "@common/types/filters/general";
import { AccountRole } from "@common/types/domains/accounting/account";
import {
    FilterTabs,
    FiltersChangeFn,
} from "@components/pageHeader/filters/lib";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { Button } from "@design-system/Button/Button";
import { CollapseIcon, ExpandIcon } from "@icons";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Loader } from "@design-system/Loader/Loader";
import { ViewTable, ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import {
    ChartOfAccountsTemplateEditorBodyHandle,
    ChartOfAccountsTemplateEditorBodyProps,
} from "../editorBodyTypes";
import { DraftAccount } from "../draftOperations";
import { useTemplateEditorRows } from "../useTemplateEditorRows";
import { useEditorSelectionGates } from "../useEditorSelectionGates";
import {
    listNavigableEditorIssues,
    useTemplateEditorIssues,
} from "../useTemplateEditorIssues";
import { buildTemplateEditorGroupOptions } from "../editorRowFilters";
import styles from "../ChartOfAccountsTemplateEditor.module.scss";
import { buildNestedRows } from "./buildNestedRows";
import { NestedEditorRow } from "./nestedRowTypes";
import {
    forEachAccountConstituent,
    forEachGroupConstituent,
} from "./nestedRowFanOut";
import {
    orRowCapabilities,
    rowConstituentDraftIds,
    selectionToDraftIds,
} from "./nestedRowConstituents";
import {
    buildChartOfAccountsTemplateNestedView,
    chartOfAccountsTemplateNestedViewConfig,
    ChartOfAccountsTemplateNestedColumn,
} from "./nestedViewConfig";
import { DisableChartOfAccountsModal } from "@components/accounting/chartOfAccounts/DisableChartOfAccountsModal";
import { EnableChartOfAccountsModal } from "@components/accounting/chartOfAccounts/EnableChartOfAccountsModal";
import { NestedEditorCommandIsland } from "./NestedEditorCommandIsland";
import { NestedEditorModals } from "./NestedEditorModals";
import { useNestedEditorAddActions } from "./useNestedEditorAddActions";
import { useNestedEditorFocusTarget } from "./useNestedEditorFocusTarget";
import { useNestedEditorSelection } from "./useNestedEditorSelection";
import { collectSelectedDraggableNestedRows } from "./nestedSelection";
import { useNestedEditorRowActions } from "./useNestedEditorRowActions";
import {
    formatNestedRowRefusalsMessage,
    NestedRowGesture,
    NestedRowRefusal,
    resolveNestedRowRefusal,
} from "./nestedRowRefusal";
import {
    isAccountPatchRefused,
    isGroupRenameRefused,
    normalizeAccountPatch,
} from "./nestedLocalRefusals";
import { PendingAddEntry } from "./nestedPendingAdds";
import { useNestedPendingAdds } from "./useNestedPendingAdds";
import { useNestedWithheldWrites } from "./useNestedWithheldWrites";
import { findGroupInScope } from "../draftGroupOperations";
import { useNestedEditorIssueFocus } from "./useNestedEditorIssueFocus";
import { useNestedEditorTableMeta } from "./useNestedEditorTableMeta";
import { useNestedEditorFooter } from "./useNestedEditorFooter";
import { useNestedFooterAddModals } from "./useNestedFooterAddModals";
import { useNestedEditorDragAndDrop } from "./useNestedEditorDragAndDrop";
import { resolveActiveNestedAddScope } from "./nestedAddScope";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { useAccountIdsWithEnabledRules } from "@hooks/useAccountIdsWithEnabledRules";

type Props = ChartOfAccountsTemplateEditorBodyProps;

const NESTED_EDITOR_AVAILABLE_FILTERS: FilterTabs[] = [
    FilterTabs.ACCOUNT_STATUS,
    FilterTabs.ACCOUNT_SOURCE,
    FilterTabs.COA_TEMPLATE_GROUPS,
];

const NESTED_EDITOR_DEFAULT_FILTERS: FiltersTypes = {};

// A rename typed into a role-holding account's name cell, held until the
// confirmation modal is answered.
interface PendingRoleRename {
    accountId: string;
    name: string;
    role: AccountRole;
}

const bindSeedRowCallback =
    (
        callback: (row: NestedEditorRow) => void,
        rowByConstituentId: Map<string, NestedEditorRow>,
    ) =>
    (seedId: string) => {
        const row = rowByConstituentId.get(seedId);
        if (row) {
            callback(row);
        }
    };

const bindOptionalSeedRowCallback = (
    callback: ((row: NestedEditorRow) => void) | undefined,
    rowByConstituentId: Map<string, NestedEditorRow>,
): ((seedId: string) => void) | undefined =>
    callback === undefined
        ? undefined
        : bindSeedRowCallback(callback, rowByConstituentId);

export const ChartOfAccountsTemplateNestedEditor = ({
    draft,
    getRowCapabilities,
    validation,
    isSaveDisabled,
    saveBlockedReason,
    isSaving,
    saveHotkeyDescription,
    onSave,
    onSaveAndClose,
    buildRows,
    displayedEntityIds,
    initialSearchFilter,
    initialFilters,
    onSearchFilterChange,
    onAccountFiltersChange,
    onEntityFilterReset,
    showSaveActions = true,
    showAccountCodes = true,
    emptyStateHeader = "No accounts in this template yet",
    accountNoun = "template",
    toolbarPrependAddon,
    toolbarAddon,
    toolbarActionsAddon,
    islandAddon,
    returnAction,
    onOperation,
    isEditingDisabled = false,
    disabledActionAddon,
    businessNamesFor,
    onMergeAccount,
    onRemoveOpeningBalance,
    onReassignRole,
    focusTarget,
    onFocusTargetConsumed,
    ref,
}: Props & { ref?: Ref<ChartOfAccountsTemplateEditorBodyHandle> }) => {
    const host = accountNoun === "chart" ? "workspace" : "template";
    const {
        accounts,
        groups,
        updateAccount,
        addAccountInScope,
        suggestCreateCodeText,
        addGroupInScope,
        createGroup,
        renameGroup,
        setAccountDisabled,
        deleteAccount,
        deleteGroup,
        moveRow,
        moveRows,
    } = draft;
    const {
        invalidNameRowIds,
        invalidGroupNameIds,
        duplicateGroupNameIds,
        duplicateNameIds,
        duplicateCodeIds,
    } = validation;

    const [searchFilter, setSearchFilter] = useState<string | undefined>(
        initialSearchFilter,
    );
    const [editorFilters, setEditorFilters] = useState<FiltersTypes>(
        initialFilters ?? {},
    );
    const [isAllExpanded, setIsAllExpanded] = useState(true);

    const tableRef = useRef<ViewTableHandle>(null);
    const { accountIdsWithEnabledRules, isLoading: isRulesLoading } =
        useAccountIdsWithEnabledRules({
            accounts,
            enabled: host === "workspace",
        });
    const isRulePresenceFilterLoading =
        editorFilters.accountRulePresence !== undefined &&
        accountIdsWithEnabledRules === undefined &&
        isRulesLoading;

    const handleFiltersChange = useCallback<FiltersChangeFn>(
        (patch) => {
            const next = { ...editorFilters, ...patch };
            setEditorFilters(next);
            onAccountFiltersChange?.({
                accountStatus: next.accountStatus,
                accountSource: next.accountSource,
                accountRolePresence: next.accountRolePresence,
                accountRulePresence: next.accountRulePresence,
                coaTemplateGroupKeys: next.coaTemplateGroupKeys,
            });
        },
        [editorFilters, onAccountFiltersChange],
    );
    const clearFilters = useCallback(() => {
        setSearchFilter(undefined);
        setEditorFilters({});
        onSearchFilterChange?.(undefined);
        onAccountFiltersChange?.({});
    }, [onSearchFilterChange, onAccountFiltersChange]);

    const getAccountRole = useCallback(
        (account: DraftAccount) => getRowCapabilities(account).role,
        [getRowCapabilities],
    );
    const { rows, visibleRows } = useTemplateEditorRows({
        accounts,
        groups,
        searchFilter,
        filters: editorFilters,
        getAccountRole,
        accountIdsWithEnabledRules,
        getRowCapabilities,
    });

    const pendingAdds = useNestedPendingAdds();

    // Business selection flows through displayedEntityIds and does not count.
    const hasActiveFilter = useMemo(() => {
        if (searchFilter && searchFilter.trim().length > 0) {
            return true;
        }
        return Object.values(editorFilters).some((value) =>
            Array.isArray(value) ? value.length > 0 : value != null,
        );
    }, [searchFilter, editorFilters]);

    const nestedRows = useMemo<NestedEditorRow[]>(
        () =>
            (buildRows ?? buildNestedRows)({
                accounts: visibleRows.map((row) => row.account),
                groups,
                pendingGestureIdByDraftId: pendingAdds.gestureIdByDraftId,
                hasActiveFilter,
            }),
        [
            buildRows,
            visibleRows,
            groups,
            pendingAdds.gestureIdByDraftId,
            hasActiveFilter,
        ],
    );

    const rowByConstituentId = useMemo(() => {
        const byId = new Map<string, NestedEditorRow>();
        const visit = (row: NestedEditorRow): void => {
            for (const draftId of rowConstituentDraftIds(row)) {
                byId.set(draftId, row);
            }
            row.children?.forEach(visit);
        };
        nestedRows.forEach(visit);
        return byId;
    }, [nestedRows]);

    // A different id space from rowByConstituentId's draft ids.
    const rowById = useMemo(() => {
        const byId = new Map<string, NestedEditorRow>();
        const visit = (row: NestedEditorRow): void => {
            byId.set(row.id, row);
            row.children?.forEach(visit);
        };
        nestedRows.forEach(visit);
        return byId;
    }, [nestedRows]);

    // Reveal + (when editable) edit the row a chart-view Edit came from.
    useNestedEditorFocusTarget({
        focusTarget,
        rowByConstituentId,
        tableRef,
        getRowCapabilities,
        onFocusTargetConsumed,
    });

    const { error: errorToast } = useToaster();
    const resolveRowRefusal = useCallback(
        ({
            row,
            gesture,
        }: {
            row: NestedEditorRow;
            gesture: NestedRowGesture;
        }): NestedRowRefusal[] =>
            host === "workspace"
                ? resolveNestedRowRefusal({
                      row,
                      gesture,
                      getRowCapabilities,
                  })
                : [],
        [host, getRowCapabilities],
    );
    const handleRowRefused = useCallback(
        (refusals: NestedRowRefusal[]) => {
            errorToast(
                formatNestedRowRefusalsMessage(refusals, businessNamesFor),
            );
        },
        [businessNamesFor, errorToast],
    );

    const withheldWrites = useNestedWithheldWrites({
        isEnabled: host === "workspace",
        accounts,
        duplicateNameIds,
        duplicateCodeIds,
        onOperation,
        pendingAdds,
    });

    // The name commit IS the write for a pending Add: one addAccount /
    // addGroup carrying the rows the click minted.
    const commitPendingAccountName = useCallback(
        (entry: PendingAddEntry, name: string) => {
            if (name.length === 0) {
                pendingAdds.take(entry.targets[0].draftId);
                entry.targets.forEach((target) =>
                    deleteAccount(target.draftId),
                );
                return;
            }
            entry.targets.forEach((target) =>
                updateAccount(target.draftId, { name }),
            );
            withheldWrites.emitPendingAdd(entry, name);
        },
        [deleteAccount, pendingAdds, updateAccount, withheldWrites],
    );

    // A business that already carries the name is a match, not a refusal:
    // drop it from the write and remove the draft row it would duplicate.
    const commitPendingGroupName = useCallback(
        (entry: PendingAddEntry, name: string) => {
            if (name.length === 0) {
                entry.targets.forEach((target) => deleteGroup(target.draftId));
                return;
            }
            const survivors = entry.targets.filter((target) => {
                const existing = findGroupInScope({
                    groups,
                    entityId: target.entityId,
                    type: entry.accountType,
                    parentGroupId: target.parentGroupId,
                    name,
                });
                if (existing !== undefined && existing.id !== target.draftId) {
                    deleteGroup(target.draftId);
                    return false;
                }
                return true;
            });
            survivors.forEach((target) => renameGroup(target.draftId, name));
            if (survivors.length === 0) {
                return;
            }
            onOperation?.({
                type: "addGroup",
                created: survivors.map((target) => ({
                    groupId: target.draftId,
                    entityId: target.entityId,
                    parentGroupId: target.parentGroupId,
                    position: target.position,
                })),
                accountType: entry.accountType,
                name,
            });
        },
        [deleteGroup, groups, onOperation, renameGroup],
    );

    const handleCancelPendingAdd = useCallback(
        (draftId: string) => {
            const entry = pendingAdds.take(draftId);
            if (entry === null) {
                return;
            }
            const remove =
                entry.kind === "account" ? deleteAccount : deleteGroup;
            entry.targets.forEach((target) => remove(target.draftId));
        },
        [deleteAccount, deleteGroup, pendingAdds],
    );

    const handleUpdateAccount = useCallback(
        (seedId: string, rawPatch: Parameters<typeof updateAccount>[1]) => {
            const row = rowByConstituentId.get(seedId);
            const isWorkspace = host === "workspace";
            const patch = isWorkspace
                ? normalizeAccountPatch(rawPatch)
                : rawPatch;
            if (isWorkspace && pendingAdds.pendingDraftIds.has(seedId)) {
                if (patch.name === undefined) {
                    // A code typed before the name lives in the draft until
                    // the create carries it.
                    if (row) {
                        forEachAccountConstituent(row, (id) =>
                            updateAccount(id, patch),
                        );
                    }
                    return;
                }
                const entry = pendingAdds
                    .getEntries()
                    .find((candidate) =>
                        candidate.targets.some(
                            (target) => target.draftId === seedId,
                        ),
                    );
                if (entry !== undefined) {
                    commitPendingAccountName(entry, patch.name);
                }
                return;
            }
            // A blank name or a second code on a collapsed row reverts the
            // cell: no draft change, no request.
            if (isWorkspace && row && isAccountPatchRefused({ row, patch })) {
                return;
            }
            const accountIds = row ? rowConstituentDraftIds(row) : [seedId];
            accountIds.forEach((id) => updateAccount(id, patch));
            if (isWorkspace) {
                withheldWrites.emitAccountPatch({ accountIds, patch });
                return;
            }
            onOperation?.({ type: "updateAccount", accountIds, patch });
        },
        [
            rowByConstituentId,
            updateAccount,
            onOperation,
            host,
            pendingAdds,
            commitPendingAccountName,
            withheldWrites,
        ],
    );
    const [pendingRoleRename, setPendingRoleRename] =
        useState<PendingRoleRename | null>(null);

    const handleRequestRenameAccount = useCallback(
        ({
            id,
            name,
            role,
        }: {
            id: string;
            name: string;
            role: AccountRole;
        }) => {
            setPendingRoleRename({ accountId: id, name, role });
        },
        [],
    );

    const handleConfirmRoleRename = useCallback(() => {
        if (pendingRoleRename === null) {
            return;
        }
        handleUpdateAccount(pendingRoleRename.accountId, {
            name: pendingRoleRename.name,
        });
        setPendingRoleRename(null);
    }, [handleUpdateAccount, pendingRoleRename]);

    const closeRoleRename = useCallback(() => setPendingRoleRename(null), []);

    const handleRenameGroup = useCallback(
        (seedId: string, rawName: string) => {
            // renameGroup trims internally; trim here too so the recorded
            // command matches the draft.
            const name = rawName.trim();
            const row = rowByConstituentId.get(seedId);
            if (
                host === "workspace" &&
                pendingAdds.pendingDraftIds.has(seedId)
            ) {
                const entry = pendingAdds.take(seedId);
                if (entry !== null) {
                    commitPendingGroupName(entry, name);
                }
                return;
            }
            // A duplicate or blank rename would 4xx and remount the editor
            // over a typo, and would leave later name paths unresolvable.
            if (
                host === "workspace" &&
                row &&
                isGroupRenameRefused({ row, name, groups })
            ) {
                return;
            }
            if (!row) {
                renameGroup(seedId, name);
                onOperation?.({
                    type: "renameGroup",
                    groupIds: [seedId],
                    name,
                });
                return;
            }
            const groupIds = rowConstituentDraftIds(row);
            forEachGroupConstituent(row, (id) => renameGroup(id, name));
            onOperation?.({ type: "renameGroup", groupIds, name });
        },
        [
            rowByConstituentId,
            renameGroup,
            onOperation,
            host,
            groups,
            pendingAdds,
            commitPendingGroupName,
        ],
    );
    const handleSetDisabledFanOut = useCallback(
        (seedId: string, isDisabled: boolean) => {
            const row = rowByConstituentId.get(seedId);
            if (row && isDisabled) {
                const refusals = resolveRowRefusal({
                    row,
                    gesture: "archive",
                });
                if (refusals.length > 0) {
                    handleRowRefused(refusals);
                    return;
                }
            }
            if (!row) {
                setAccountDisabled(seedId, isDisabled);
                onOperation?.({
                    type: "setAccountDisabled",
                    accountIds: [seedId],
                    isDisabled,
                });
                return;
            }
            const accountIds = rowConstituentDraftIds(row);
            forEachAccountConstituent(row, (id) =>
                setAccountDisabled(id, isDisabled),
            );
            onOperation?.({
                type: "setAccountDisabled",
                accountIds,
                isDisabled,
            });
        },
        [
            rowByConstituentId,
            setAccountDisabled,
            onOperation,
            resolveRowRefusal,
            handleRowRefused,
        ],
    );

    // Workspace Archive/Enable confirm before writing (the chart page's
    // shared modal). Template host keeps the immediate fan-out.
    const [setDisabledConfirm, setSetDisabledConfirm] = useState<{
        row: NestedEditorRow;
        isDisabled: boolean;
    } | null>(null);
    const handleRequestSetDisabled = useCallback(
        (seedId: string, isDisabled: boolean) => {
            const row = rowByConstituentId.get(seedId) ?? null;
            if (host !== "workspace" || !row) {
                handleSetDisabledFanOut(seedId, isDisabled);
                return;
            }
            setSetDisabledConfirm({ row, isDisabled });
        },
        [host, rowByConstituentId, handleSetDisabledFanOut],
    );
    const confirmSetDisabled = useCallback(() => {
        if (setDisabledConfirm === null) {
            return;
        }
        const { row, isDisabled } = setDisabledConfirm;
        const [firstAccount] = row.accounts;
        if (firstAccount) {
            handleSetDisabledFanOut(firstAccount.id, isDisabled);
        }
    }, [setDisabledConfirm, handleSetDisabledFanOut]);

    const orGetRowCapabilities = useCallback<typeof getRowCapabilities>(
        (account) => {
            const row = rowByConstituentId.get(account.id);
            return row
                ? orRowCapabilities(row, getRowCapabilities)
                : getRowCapabilities(account);
        },
        [rowByConstituentId, getRowCapabilities],
    );

    const groupFilterOptions = useMemo(
        () =>
            buildTemplateEditorGroupOptions({
                rows,
                groups,
                displayedEntityIds,
            }),
        [rows, groups, displayedEntityIds],
    );

    const selection = useNestedEditorSelection({
        nestedRows,
        pendingDraftIds: pendingAdds.pendingDraftIds,
    });

    // Bulk gates and bulk delete need these: merge-view display keys don't
    // intersect the flat draft.
    const selectionDraftIds = useMemo(
        () => selectionToDraftIds(nestedRows, selection.rowSelection),
        [nestedRows, selection.rowSelection],
    );
    const selectedAccountIds = useMemo(() => {
        const accountIdSet = new Set(accounts.map((a) => a.id));
        return selectionDraftIds.filter((id) => accountIdSet.has(id));
    }, [selectionDraftIds, accounts]);
    const selectedConstituentGroupIds = useMemo(() => {
        const groupIdSet = new Set(groups.map((g) => g.id));
        return selectionDraftIds.filter((id) => groupIdSet.has(id));
    }, [selectionDraftIds, groups]);

    const gates = useEditorSelectionGates({
        accounts,
        visibleRows,
        rowSelection: selection.rowSelection,
        getRowCapabilities: orGetRowCapabilities,
        selectedAccountIds,
    });

    useImperativeHandle(
        ref,
        () => ({ clearSelection: selection.clearSelection }),
        [selection.clearSelection],
    );

    const builtIssues = useTemplateEditorIssues({
        rows,
        invalidNameRowIds,
        duplicateNameRowIds: duplicateNameIds,
        duplicateCodeIds,
        getRowCapabilities: orGetRowCapabilities,
        groups,
        invalidGroupNameIds,
        duplicateGroupNameIds,
    });

    const isRowBusinessHidden = useCallback(
        (draftId: string) => {
            if (displayedEntityIds === undefined) {
                return false;
            }
            const entityId =
                accounts.find((account) => account.id === draftId)?.entityId ??
                groups.find((group) => group.id === draftId)?.entityId;
            return (
                entityId !== undefined && !displayedEntityIds.includes(entityId)
            );
        },
        [accounts, groups, displayedEntityIds],
    );

    const issues = useMemo(
        () =>
            listNavigableEditorIssues({
                issues: builtIssues,
                onEntityFilterReset,
                isRowBusinessHidden,
            }),
        [builtIssues, onEntityFilterReset, isRowBusinessHidden],
    );

    const issueFocus = useNestedEditorIssueFocus({
        issues,
        tableRef,
        clearFilters,
        resolveRowId: (draftId) =>
            rowByConstituentId.get(draftId)?.id ?? draftId,
        isRowBusinessHidden,
        resetBusinessFilter: onEntityFilterReset,
    });

    const { handleAddAccount, handleAddGroup } = useNestedEditorAddActions({
        tableRef,
        clearFilters,
        groups,
        addAccountInScope,
        suggestCreateCodeText,
        addGroupInScope,
        resolveFocusRowId: (draftId) =>
            rowByConstituentId.get(draftId)?.id ?? draftId,
        host,
        onPendingAdd: pendingAdds.register,
        onOperation,
    });

    const footerAdd = useNestedFooterAddModals({
        tableRef,
        nestedRows,
        rowById,
        displayedEntityIds,
        groups,
        addAccountInScope,
        suggestCreateCodeText,
        updateAccount,
        createGroup,
        onOperation,
    });

    // Display rows, not flat ids: each one is its own transaction on the
    // workspace host, and its own refusal.
    const selectedRows = useMemo(
        () =>
            Object.entries(selection.rowSelection)
                .filter(([, isSelected]) => isSelected)
                .map(([rowId]) => rowById.get(rowId))
                .filter((row): row is NestedEditorRow => row !== undefined),
        [selection.rowSelection, rowById],
    );

    const rowActions = useNestedEditorRowActions({
        setAccountDisabled,
        deleteAccount,
        deleteGroup,
        selectedVisibleAccountIds: gates.selectedVisibleAccountIds,
        deletableSelectedAccountIds: gates.deletableSelectedAccountIds,
        disablableSelectedAccountIds: gates.disablableSelectedAccountIds,
        selectedGroupIds: selectedConstituentGroupIds,
        clearSelection: selection.clearSelection,
        accountNoun,
        host,
        selectedRows,
        resolveRowRefusal,
        onRowRefused: handleRowRefused,
        businessNamesFor,
        onOperation,
    });

    const handleRequestDeleteAccount = useCallback(
        (seedId: string) => {
            const row = rowByConstituentId.get(seedId) ?? null;
            rowActions.requestDeleteAccount({
                ids: row ? rowConstituentDraftIds(row) : [seedId],
                row,
            });
        },
        [rowByConstituentId, rowActions],
    );
    const handleRequestDeleteGroup = useCallback(
        (seedId: string) => {
            const row = rowByConstituentId.get(seedId) ?? null;
            rowActions.requestDeleteGroup({
                ids: row ? rowConstituentDraftIds(row) : [seedId],
                row,
            });
        },
        [rowByConstituentId, rowActions],
    );

    const handleMergeAccount = useMemo(
        () => bindOptionalSeedRowCallback(onMergeAccount, rowByConstituentId),
        [rowByConstituentId, onMergeAccount],
    );
    const handleRemoveOpeningBalance = useMemo(
        () =>
            bindOptionalSeedRowCallback(
                onRemoveOpeningBalance,
                rowByConstituentId,
            ),
        [rowByConstituentId, onRemoveOpeningBalance],
    );
    const handleReassignRole = useMemo(
        () => bindSeedRowCallback(onReassignRole, rowByConstituentId),
        [rowByConstituentId, onReassignRole],
    );

    const tableMeta = useNestedEditorTableMeta({
        onUpdateAccount: handleUpdateAccount,
        onRequestRenameAccount: handleRequestRenameAccount,
        onRenameGroup: handleRenameGroup,
        onAddAccount: handleAddAccount,
        onAddGroup: handleAddGroup,
        onSetDisabled: handleRequestSetDisabled,
        onDeleteAccount: handleRequestDeleteAccount,
        onDeleteGroup: handleRequestDeleteGroup,
        onCancelPendingAdd: handleCancelPendingAdd,
        onMergeAccount: handleMergeAccount,
        onRemoveOpeningBalance: handleRemoveOpeningBalance,
        onReassignRole: handleReassignRole,
        getRowCapabilities: orGetRowCapabilities,
        getConstituentCapabilities: getRowCapabilities,
        duplicateCodeIds,
        duplicateGroupNameIds,
        duplicateNameIds,
        displayedEntityIds,
        host,
        pendingDraftIds: pendingAdds.pendingDraftIds,
        isEditingDisabled,
    });

    const activeAddScope = useMemo(
        () =>
            resolveActiveNestedAddScope({
                nestedRows,
                rowSelection: selection.rowSelection,
                displayedEntityIds,
            }),
        [nestedRows, selection.rowSelection, displayedEntityIds],
    );

    // The active scope's parentGroupId is a constituent group id; the footer
    // modal wants a nested-row id.
    const activeParentRowId =
        activeAddScope.parentGroupId == null
            ? null
            : (rowByConstituentId.get(activeAddScope.parentGroupId)?.id ??
              null);
    const onAddGroup = useCallback(
        () =>
            footerAdd.openAddGroup({
                type: activeAddScope.type,
                parentRowId: activeParentRowId,
            }),
        [footerAdd, activeAddScope.type, activeParentRowId],
    );
    const onAddAccount = useCallback(
        () =>
            footerAdd.openAddAccount({
                type: activeAddScope.type,
                parentRowId: activeParentRowId,
            }),
        [footerAdd, activeAddScope.type, activeParentRowId],
    );

    const { islandProps } = useNestedEditorFooter({
        selection,
        gates,
        host,
        rowActions,
        issues,
        issueFocus,
        onAddGroup,
        onAddAccount,
        isAddModalOpen: footerAdd.isAddModalOpen,
        isSaving,
        isSaveDisabled,
        saveBlockedReason,
        saveHotkeyDescription,
        showSaveActions,
        onSave,
        onSaveAndClose,
        isEditingDisabled,
    });

    // Reordering needs the full sibling set, so it is off while filtered.
    const isTreeFiltered = hasActiveFilter;

    const selectedDraggableRows = useMemo(
        () =>
            collectSelectedDraggableNestedRows({
                rows: nestedRows,
                rowSelection: selection.rowSelection,
            }),
        [nestedRows, selection.rowSelection],
    );

    const rowDragAndDrop = useNestedEditorDragAndDrop({
        accounts,
        groups,
        getRowCapabilities: orGetRowCapabilities,
        moveRow,
        moveRows,
        createGroup,
        selectedDraggableRows,
        nestedRows,
        displayedEntityIds,
        disabled: isTreeFiltered || isEditingDisabled,
        pendingDraftIds: pendingAdds.pendingDraftIds,
        onOperation,
    });

    // Undefined `displayedEntityIds` already marks a template host
    // elsewhere in the body.
    const isEntityScoped = displayedEntityIds !== undefined;
    const view = useMemo(
        () =>
            buildChartOfAccountsTemplateNestedView({
                showEntities: isEntityScoped,
                showCode: showAccountCodes,
                showForm1099Box: host === "template",
            }),
        [isEntityScoped, showAccountCodes, host],
    );

    const handleToggleExpandAll = useCallback(() => {
        if (isAllExpanded) {
            tableRef.current?.collapseAll();
            setIsAllExpanded(false);
            return;
        }
        tableRef.current?.expandAll();
        setIsAllExpanded(true);
    }, [isAllExpanded]);

    return (
        <div
            className={classNames(
                styles.drawerContent,
                styles.centeredSelectColumn,
            )}
        >
            <TablePageHeader
                isFullPage
                prependChildren={toolbarPrependAddon}
                search={searchFilter}
                onSearchChange={(value) => {
                    const next = value || undefined;
                    setSearchFilter(next);
                    onSearchFilterChange?.(next);
                }}
                searchKeyboardShortcuts={false}
                hasSearchFindShortcut
                filters={editorFilters}
                onFiltersChange={handleFiltersChange}
                availableFilters={[
                    ...NESTED_EDITOR_AVAILABLE_FILTERS,
                    FilterTabs.ACCOUNT_ROLE_PRESENCE,
                    ...(host === "workspace"
                        ? [FilterTabs.ACCOUNT_RULE_PRESENCE]
                        : []),
                ]}
                defaultFilters={NESTED_EDITOR_DEFAULT_FILTERS}
                defaultTab={FilterTabs.ACCOUNT_STATUS}
                staticTemplateGroupOptions={groupFilterOptions}
            >
                {toolbarActionsAddon}
                <Tooltip
                    key={isAllExpanded ? "collapse" : "expand"}
                    content={isAllExpanded ? "Collapse all" : "Expand all"}
                >
                    <Button
                        variant="primary"
                        size="md"
                        iconOnly
                        onClick={handleToggleExpandAll}
                        aria-label={
                            isAllExpanded ? "Collapse all" : "Expand all"
                        }
                    >
                        {isAllExpanded ? <CollapseIcon /> : <ExpandIcon />}
                    </Button>
                </Tooltip>
                {toolbarAddon}
                {disabledActionAddon}
            </TablePageHeader>

            <ViewTable<
                NestedEditorRow,
                ChartOfAccountsTemplateNestedColumn,
                never
            >
                ref={tableRef}
                data={isRulePresenceFilterLoading ? null : nestedRows}
                view={view}
                config={chartOfAccountsTemplateNestedViewConfig}
                tableMeta={tableMeta}
                rowSelection={selection.rowSelection}
                onRowSelectionChange={selection.handleRowSelectionChange}
                enableRowSelection={
                    isEditingDisabled ? false : selection.enableRowSelection
                }
                enableSubRowSelection={false}
                autoExpandOnMount
                nestingIndentPx={24}
                enableColumnResizing
                rowDragAndDrop={rowDragAndDrop}
                loadingView={() => <Loader />}
                emptyView={() => <EmptyState header={emptyStateHeader} />}
                footer={
                    <NestedEditorCommandIsland
                        {...islandProps}
                        islandAddon={islandAddon}
                        returnAction={returnAction}
                    />
                }
            />

            <NestedEditorModals
                groups={groups}
                addAccountModalProps={footerAdd.addAccountModalProps}
                addGroupModalProps={footerAdd.addGroupModalProps}
                isDeleteOpen={rowActions.pendingDelete !== null}
                deleteTitle={rowActions.deleteModalTitle}
                deleteQuestion={rowActions.deleteModalQuestion}
                onDeleteConfirm={rowActions.handleConfirmDelete}
                onDeleteClose={rowActions.closeDelete}
                roleRenameRole={pendingRoleRename?.role ?? null}
                onRoleRenameConfirm={handleConfirmRoleRename}
                onRoleRenameClose={closeRoleRename}
            />
            {setDisabledConfirm !== null &&
                (setDisabledConfirm.isDisabled ? (
                    <DisableChartOfAccountsModal
                        show
                        onHide={() => setSetDisabledConfirm(null)}
                        accounts={setDisabledConfirm.row.accounts}
                        onConfirm={confirmSetDisabled}
                    />
                ) : (
                    <EnableChartOfAccountsModal
                        show
                        onHide={() => setSetDisabledConfirm(null)}
                        accounts={setDisabledConfirm.row.accounts}
                        onConfirm={confirmSetDisabled}
                    />
                ))}
        </div>
    );
};
